import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api, { trackActivity, portfolioRegistryAPI } from '../services/api';
import './PortfolioDashboardApp.css';
import {
  RISK_METHOD_NOTE, buildSourceLines, registrySourceMap, fyGapNotes, riskGapNotes,
} from '../utils/performanceDisclosure';
import useDisclaimer, { disclaimerStateText } from '../hooks/useDisclaimer';

// Default portfolios for Drummond users
const DRUMMOND_DEFAULT_PORTFOLIOS = [
  'DRU005SU', 'DRU006SU', 'DRU007SU', 'DRU008SU',
  'FLPDHIG1', 'DSP100PLUS', 'DDP',
];

// Registry suite whose tab shows the house (DS-average) SAA positioning table
const STRATEGIC_SUITE_LABEL = 'Strategic Series';

// Factsheet display names for Drummond Strategic Series
const DRUMMOND_DISPLAY_NAMES = {
  DRU005SU: 'DS 30 Portfolio',
  DRU006SU: 'DS 50 Portfolio',
  DRU007SU: 'DS 70 Portfolio',
  DRU008SU: 'DS 90 Portfolio',
  FLPDHIG1: 'DS 100 Portfolio',
  DSP100PLUS: 'DS 100 Plus',
  DDP: 'Drummond Dynamic Portfolio',
};

// Growth asset classes for the summary row
// Include both split ('Global Alpha - G') and merged ('Global Alpha') forms
// to match the factsheet's get_neutral_growth_exposure logic
const GROWTH_ASSET_CLASSES = [
  'Australian Equities', 'International Equities',
  'Real Estate', 'Infrastructure', 'Global Alpha - G', 'Global Alpha',
];

// Merge EM Equities into International Equities at the frontend level
const MERGE_ASSET_CLASSES = { 'EM Equities': 'International Equities' };

// Parse a published growth band from the registry (e.g. "0-20%", "60 - 80")
// into { lo, hi }. Returns null if it isn't a numeric low-high band.
const parseGrowthBand = (raw) => {
  if (!raw) return null;
  const m = String(raw).replace(/%/g, '').match(/(-?\d+(?:\.\d+)?)\s*[-–]\s*(-?\d+(?:\.\d+)?)/);
  if (!m) return null;
  const lo = Number(m[1]);
  const hi = Number(m[2]);
  if (Number.isNaN(lo) || Number.isNaN(hi)) return null;
  return { lo: Math.min(lo, hi), hi: Math.max(lo, hi) };
};

// Render a "lo-hi" range band with per-number percent signs, e.g. "0-20" -> "0%-20%".
// Leaves non-numeric placeholders (e.g. "—") untouched.
const formatBandPct = (band) => {
  if (!band || !/\d/.test(band)) return band ?? '—';
  return String(band).replace(/(-?\d+(?:\.\d+)?)/g, '$1%');
};

// Asset classes that use a 5% strong threshold; all others use 3%
const STANDARD_THRESHOLD_ACS = new Set([
  'Australian Equities', 'International Equities',
  'Corporate Debt', 'Government Bonds',
  'Total Growth vs Defensive', 'Cash',
]);

// Positioning thresholds.
// `exposure` is the actual target weight (%) for the asset class group. When an
// ACG carries an SAA (neutral) weight but the portfolio holds none of it (0%
// exposure), that's a strong underweight regardless of how small the SAA weight
// is — so the magnitude-based thresholds below are bypassed in that case.
const getPosition = (tilt, assetClass, exposure = null) => {
  const strongThreshold = STANDARD_THRESHOLD_ACS.has(assetClass) ? 5 : 3;
  // neutral (SAA) weight = exposure − tilt. Zero exposure against a positive SAA
  // weight => strong underweight.
  if (exposure != null && exposure < 0.5 && (exposure - tilt) > 0.5) return 'strongUW';
  if (tilt > strongThreshold) return 'strongOW';
  if (tilt >= 0.5) return 'overweight';
  if (Math.abs(tilt) < 0.5) return 'neutral';
  if (tilt <= -strongThreshold) return 'strongUW';
  return 'underweight';
};

const POSITION_COLUMNS = ['strongUW', 'underweight', 'neutral', 'overweight', 'strongOW'];
const POSITION_LABELS = {
  strongUW: 'Strong Underweight',
  underweight: 'Underweight',
  neutral: 'Neutral',
  overweight: 'Overweight',
  strongOW: 'Strong Overweight',
};
const POSITION_DOT_COLORS = {
  strongUW: '#8B0000',
  underweight: '#dc3545',
  neutral: '#072040',
  overweight: '#91CF9B',
  strongOW: '#2d8a4e',
};

const PERF_COLUMNS = [
  { key: 'mtd', label: 'MTD' },
  { key: 'return_1m', label: '1M' },
  { key: 'return_3m', label: '3M' },
  { key: 'return_6m', label: '6M' },
  { key: 'return_12m', label: '12M' },
  { key: 'return_3yr', label: '3Yr' },
  { key: 'return_5yr', label: '5Yr' },
];

// Performance periods shown in Panel 3 (matching API keys)
const PERF_TABLE_PERIODS = [
  { key: '1 Month', label: '1 Month' },
  { key: '3 Month', label: '3 Month' },
  { key: '6 Month', label: '6 Month' },
  { key: '12 Month', label: '1 Year' },
  { key: '2 Year', label: '2 Year' },
  { key: '3 Year', label: '3 Year' },
  { key: '5 Year', label: '5 Year' },
  { key: 'Annualised Inception', label: 'Inception (p.a.)' },
];

const fmtPct = (val) => {
  if (val === null || val === undefined) return '—';
  return `${Number(val).toFixed(2)}%`;
};

const fmtPct1 = (val) => {
  if (val === null || val === undefined) return '—';
  return `${Number(val).toFixed(1)}%`;
};

// ─── Main Component ───────────────────────────────────────────────
const PortfolioDashboardApp = () => {
  // General performance note: managed in Admin > Disclaimers (perf_general_note)
  const perfNote = useDisclaimer('perf_general_note');
  const { user } = useAuth();
  const isDrummondUser = user?.email?.endsWith('@drummondcp.com') || user?.email?.endsWith('@drummond.com');

  // State
  const [portfolioList, setPortfolioList] = useState([]);
  const [holdingsData, setHoldingsData] = useState(null);
  const [fundPerfData, setFundPerfData] = useState({});
  const [tiltsData, setTiltsData] = useState(null);
  const [expandedAssetClasses, setExpandedAssetClasses] = useState({});
  const [hoveredFund, setHoveredFund] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [performanceData, setPerformanceData] = useState(null);
  const [fyReturnsData, setFyReturnsData] = useState({}); // { code: { columns: [...] } }
  const [perfView, setPerfView] = useState('periods'); // 'periods' | 'fy' | 'risk'
  const [riskMetricsData, setRiskMetricsData] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingHoldings, setLoadingHoldings] = useState(false);
  const [loadingTilts, setLoadingTilts] = useState(false);
  const [loadingPerf, setLoadingPerf] = useState(false);
  const [loadingFY, setLoadingFY] = useState(false);
  const [loadingRisk, setLoadingRisk] = useState(false);
  const [registryData, setRegistryData] = useState(null);
  const [allocationData, setAllocationData] = useState({});  // { code: [{ asset_class, weight }] } — SAA weights from factsheet
  const [blenderData, setBlenderData] = useState({});        // { code: { return, volatility, icr, ... } }
  const [loadingSAA, setLoadingSAA] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSuite, setSelectedSuite] = useState(null);

  // Derived: code-to-name map (Drummond users get factsheet Strategic Series names)
  const codeToName = useMemo(() => {
    const map = {};
    portfolioList.forEach((p) => {
      if (isDrummondUser && DRUMMOND_DISPLAY_NAMES[p.portfolio_code]) {
        map[p.portfolio_code] = DRUMMOND_DISPLAY_NAMES[p.portfolio_code];
      } else {
        map[p.portfolio_code] = p.display_name || p.portfolio_name;
      }
    });
    return map;
  }, [portfolioList, isDrummondUser]);

  // Benchmark display name per portfolio code, taken from the main performance payload
  // (the 1/3/5-year tab). Reused by the Financial Year and Risk tabs so all three show the
  // same benchmark name instead of a generic "Benchmark" label.
  const codeToBenchmark = useMemo(() => {
    const map = {};
    (performanceData?.portfolios || []).forEach((pf) => {
      if (pf.ami_benchmark) map[pf.portfolio_code] = pf.ami_benchmark;
    });
    return map;
  }, [performanceData]);

  // Derived: portfolio codes to use (all available — suite tabs handle grouping)
  const allCodes = useMemo(() => {
    return portfolioList.map((p) => p.portfolio_code);
  }, [portfolioList]);

  // Derive available suite tabs from registry × allCodes
  const availableTabs = useMemo(() => {
    if (!registryData?.suites || allCodes.length === 0) return [];
    const tabs = [];
    const coveredCodes = new Set();
    registryData.suites.forEach((suite) => {
      const included = suite.portfolios.filter((p) => allCodes.includes(p.portfolio_code));
      const suiteCodes = included.map((p) => p.portfolio_code);
      if (suiteCodes.length > 0) {
        tabs.push({
          label: suite.label,
          codes: suiteCodes,
          tailored: included.some((p) => p.tailored),
          clients: suite.clients || [],   // client / brand behind a tailored suite (Drummond only)
        });
        suiteCodes.forEach((c) => coveredCodes.add(c));
      }
    });
    if (!isDrummondUser) {
      const customCodes = allCodes.filter((code) => !coveredCodes.has(code));
      if (customCodes.length > 0) {
        tabs.push({ label: 'Custom', codes: customCodes });
      }
    }
    return tabs;
  }, [registryData, allCodes]);

  // Auto-select first tab
  useEffect(() => {
    if (availableTabs.length > 0 && selectedSuite === null) {
      setSelectedSuite(availableTabs[0].label);
    }
  }, [availableTabs, selectedSuite]);

  // Filtered codes for the selected suite tab
  const filteredCodes = useMemo(() => {
    if (availableTabs.length === 0) return allCodes;
    const tab = availableTabs.find((t) => t.label === selectedSuite);
    return tab ? tab.codes : availableTabs[0].codes;
  }, [availableTabs, selectedSuite, allCodes]);

  // ─── Mount: load portfolio list + registry ──────────────────────
  useEffect(() => {
    trackActivity('app_access', 'portfolio_dashboard');
    Promise.all([
      api.get('/api/portfolio/portfolios', { params: { include_approved_targets: true } }),
      portfolioRegistryAPI.getRegistry('portfolio_viewer').catch(() => null),
    ])
      .then(([portfoliosRes, registryRes]) => {
        setPortfolioList(portfoliosRes.data);
        if (registryRes) setRegistryData(registryRes);
        setInitialLoading(false);
      })
      .catch((err) => {
        console.error('Error loading portfolios:', err);
        setError('Failed to load portfolios. Please check the backend is running.');
        setInitialLoading(false);
      });
  }, []);

  // ─── Fetch tilts for the selected suite ────────────────────────
  // The SAA Positioning table only shows for the Strategic Series (the house
  // average across the DS portfolios) and for tailored suites, where every
  // portfolio in the suite is averaged against its own master SAA using the
  // same tilt logic. Other suites don't show the table, so nothing is fetched.
  const tiltsSeqRef = useRef(0);
  const activeTab = useMemo(
    () => availableTabs.find((t) => t.label === selectedSuite) || null,
    [availableTabs, selectedSuite]
  );
  const showPositioning = selectedSuite === STRATEGIC_SUITE_LABEL || !!activeTab?.tailored;

  useEffect(() => {
    if (!showPositioning) { setTiltsData(null); return; }
    const seq = ++tiltsSeqRef.current;   // newest tab wins; stale responses ignored
    setLoadingTilts(true);
    const params = activeTab?.tailored ? { portfolio_codes: activeTab.codes.join(',') } : {};
    api.get('/api/portfolio/portfolios/average-tilts', { params })
      .then((r) => { if (seq === tiltsSeqRef.current) setTiltsData(r.data); })
      .catch(() => { if (seq === tiltsSeqRef.current) setTiltsData(null); })
      .finally(() => { if (seq === tiltsSeqRef.current) setLoadingTilts(false); });
  }, [activeTab, showPositioning]);

  // ─── Fetch holdings + performance for selected tab ────────────
  useEffect(() => {
    if (filteredCodes.length === 0) return;
    const codesParam = filteredCodes.join(',');

    setLoadingHoldings(true);
    api.get('/api/portfolio/portfolios/holdings', { params: { portfolio_codes: codesParam } })
      .then((r) => setHoldingsData(r.data))
      .catch(() => setHoldingsData(null))
      .finally(() => setLoadingHoldings(false));

    setLoadingPerf(true);
    api.get('/api/portfolio/portfolios/performance', { params: { portfolio_codes: codesParam, hide_flagged_performance: true } })
      .then((r) => setPerformanceData(r.data))
      .catch(() => setPerformanceData(null))
      .finally(() => setLoadingPerf(false));
  }, [filteredCodes]);

  // ─── Registry: code-to-blender mapping ──────────────────────────
  const codeToBlender = useMemo(() => {
    if (!registryData?.suites) return {};
    const map = {};
    registryData.suites.forEach((suite) => {
      suite.portfolios.forEach((p) => {
        if (p.portfolio_code && p.blender_code) {
          map[p.portfolio_code] = p.blender_code;
        }
      });
    });
    return map;
  }, [registryData]);

  const codeToGrowthExposure = useMemo(() => {
    if (!registryData?.suites) return {};
    const map = {};
    registryData.suites.forEach((suite) => {
      suite.portfolios.forEach((p) => {
        if (p.portfolio_code && p.growth_exposure != null) {
          map[p.portfolio_code] = p.growth_exposure;
        }
      });
    });
    return map;
  }, [registryData]);

  // Published growth-exposure band (e.g. "0-20%") set per portfolio in the
  // registry. When present it's the source of truth for the growth/defensive
  // ranges; otherwise the dashboard falls back to computing neutral ±10.
  const codeToGrowthRange = useMemo(() => {
    if (!registryData?.suites) return {};
    const map = {};
    registryData.suites.forEach((suite) => {
      suite.portfolios.forEach((p) => {
        if (p.portfolio_code && p.growth_range) {
          map[p.portfolio_code] = p.growth_range;
        }
      });
    });
    return map;
  }, [registryData]);

  // ─── Build dynamic inception date disclaimer from registry ──────
  const inceptionDisclaimer = useMemo(() => {
    if (!registryData?.suites) return '';
    // Collect inception dates for portfolios in the current suite tab
    const tab = availableTabs.find((t) => t.label === selectedSuite);
    const tabCodes = tab ? new Set(tab.codes) : new Set(filteredCodes);
    const dateGroups = {};  // { "1/11/2019": ["DS 30 Portfolio", "DS 50 Portfolio"] }
    registryData.suites.forEach((suite) => {
      suite.portfolios.forEach((p) => {
        if (!tabCodes.has(p.portfolio_code) || !p.inception_date) return;
        const d = new Date(p.inception_date);
        const formatted = `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
        const name = codeToName[p.portfolio_code] || p.display_name;
        if (!dateGroups[formatted]) dateGroups[formatted] = [];
        dateGroups[formatted].push(name);
      });
    });
    const entries = Object.entries(dateGroups);
    if (entries.length === 0) return '';
    // If all portfolios share the same date, use a single statement
    if (entries.length === 1) {
      return `Inception date is ${entries[0][0]}.`;
    }
    // Otherwise list each group
    return entries.map(([date, names]) =>
      `Inception date for ${names.join(' & ')} is ${date}.`
    ).join(' ');
  }, [registryData, availableTabs, selectedSuite, filteredCodes, codeToName]);

  // ─── Performance-source lines for the portfolios in view (from registry) ──
  // Each portfolio's own `performance_source` (Investment Config) — suites can mix
  // platforms and fee bases (e.g. Compass: AMP North vs BT Panorama), so a single
  // suite-level value would misdescribe some of them.
  // Only portfolios whose performance is actually shown (same rule as the Periods
  // table: some period has a non-zero return) — a hidden row needs no disclosure.
  const performanceSourceLines = useMemo(() => {
    if (!registryData?.suites) return [];
    const shown = (performanceData?.portfolios || [])
      .filter((pf) => filteredCodes.includes(pf.portfolio_code))
      .filter((pf) => PERF_TABLE_PERIODS.some((p) => {
        const v = (pf.performance || {})[p.key]?.portfolio;
        return v != null && Number(v) !== 0;
      }))
      .map((pf) => pf.portfolio_code);
    return buildSourceLines(shown, registrySourceMap(registryData), (c) => codeToName[c] || c);
  }, [registryData, performanceData, filteredCodes, codeToName]);

  // ─── Fetch SAA weights (factsheet) + blender for selected tab ──
  useEffect(() => {
    if (filteredCodes.length === 0) return;

    // Only fetch codes not already cached
    const newCodes = filteredCodes.filter((c) => !allocationData[c]);
    const newBlenderCodes = filteredCodes.filter((c) => !blenderData[c] && codeToBlender[c]);
    if (newCodes.length === 0 && newBlenderCodes.length === 0) { setLoadingSAA(false); return; }

    setLoadingSAA(true);

    // SAA allocation + FY returns: single batched call. Replaces the previous
    // per-code /factsheets/portfolio/{code} fan-out, which ran ~13 queries each
    // just to extract these two fields.
    const saaPromise = newCodes.length > 0
      ? api.get('/api/portfolio/portfolios/saa-summary', { params: { portfolio_codes: newCodes.join(',') } })
          .then((r) => r.data?.portfolios || {})
          .catch(() => ({}))
      : Promise.resolve({});

    // Blender: single batch call instead of N individual calls
    const blenderPromise = newBlenderCodes.length > 0
      ? api.post('/api/blender/batch-summary', {
          codes: Object.fromEntries(newBlenderCodes.map((c) => [c, codeToBlender[c]])),
        }).then((r) => r.data).catch(() => ({}))
      : Promise.resolve({});

    Promise.all([saaPromise, blenderPromise])
      .then(([saaResults, blenderResults]) => {
        const allocMap = {};
        const fyMap = {};
        Object.entries(saaResults).forEach(([code, data]) => {
          if (data?.saa_allocation) {
            allocMap[code] = data.saa_allocation.map((r) => ({
              asset_class_group: r.asset_class,
              target_percent: r.weight,
            }));
          }
          if (data?.fy_returns) {
            fyMap[code] = data.fy_returns;
          }
        });
        setAllocationData((prev) => ({ ...prev, ...allocMap }));
        if (Object.keys(fyMap).length > 0) {
          setFyReturnsData((prev) => ({ ...prev, ...fyMap }));
        }

        setBlenderData((prev) => ({ ...prev, ...blenderResults }));
      })
      .finally(() => setLoadingSAA(false));
  }, [filteredCodes, codeToBlender]);

  // ─── Fetch fund performance after holdings arrive ──────────────
  useEffect(() => {
    if (!holdingsData?.holdings?.length) {
      setFundPerfData({});
      return;
    }
    const trCodes = [...new Set(holdingsData.holdings.map((h) => h.tr_code).filter(Boolean))];
    if (trCodes.length === 0) return;

    api.post('/api/screener/funds-by-tr-codes', { tr_codes: trCodes })
      .then((res) => setFundPerfData(res.data.funds || {}))
      .catch(() => setFundPerfData({}));
  }, [holdingsData]);

  // ─── Fetch FY returns when toggled (lazy) ───────────────────────
  useEffect(() => {
    if (perfView !== 'fy' || filteredCodes.length === 0) return;
    const uncached = filteredCodes.filter((c) => !fyReturnsData[c]);
    if (uncached.length === 0) return;

    setLoadingFY(true);
    api.get('/api/portfolio/portfolios/saa-summary', { params: { portfolio_codes: uncached.join(',') } })
      .then((r) => {
        const portfolios = r.data?.portfolios || {};
        const map = {};
        Object.entries(portfolios).forEach(([code, data]) => {
          if (data?.fy_returns) map[code] = data.fy_returns;
        });
        setFyReturnsData((prev) => ({ ...prev, ...map }));
      })
      .catch(() => {})
      .finally(() => setLoadingFY(false));
  }, [perfView, filteredCodes, fyReturnsData]);

  // ─── Fetch risk metrics when toggled (lazy) ───────────────────
  useEffect(() => {
    if (perfView !== 'risk' || filteredCodes.length === 0) return;
    const uncached = filteredCodes.filter((c) => !riskMetricsData?.[c]);
    if (uncached.length === 0) return;

    setLoadingRisk(true);
    const codesParam = uncached.join(',');
    api.get('/api/portfolio/portfolios/risk-metrics', { params: { portfolio_codes: codesParam, hide_flagged_performance: true } })
      .then((r) => setRiskMetricsData((prev) => ({ ...prev, ...(r.data?.portfolios || {}) })))
      .catch(() => {})
      .finally(() => setLoadingRisk(false));
  }, [perfView, filteredCodes, riskMetricsData]);

  // Derive unified FY columns (superset across all portfolios, sorted)
  const fyColumns = useMemo(() => {
    if (Object.keys(fyReturnsData).length === 0) return [];
    const keySet = new Map();
    filteredCodes.forEach((code) => {
      (fyReturnsData[code]?.columns || []).forEach((col) => {
        if (!keySet.has(col.key)) keySet.set(col.key, col);
      });
    });
    return Array.from(keySet.values()).sort((a, b) => {
      const numA = parseInt(a.key.replace(/\D/g, ''), 10) || 0;
      const numB = parseInt(b.key.replace(/\D/g, ''), 10) || 0;
      return numA - numB;
    });
  }, [fyReturnsData, filteredCodes]);

  // Codes with usable financial-year data: at least one FY column whose portfolio
  // return is non-null and non-zero. An all-zero (or all-null) portfolio has no real
  // return data, so it's hidden entirely rather than shown as a wall of 0.0%.
  const fyVisibleCodes = useMemo(() => filteredCodes.filter((code) => {
    const cols = fyReturnsData[code]?.columns;
    return Array.isArray(cols) && cols.some((c) => c.portfolio != null && Number(c.portfolio) !== 0);
  }), [filteredCodes, fyReturnsData]);

  // Codes with usable risk metrics: at least one portfolio-level metric that is
  // non-null and non-zero. Same all-zero suppression as the performance table.
  const riskVisibleCodes = useMemo(() => filteredCodes.filter((code) => {
    const d = riskMetricsData?.[code];
    if (!d || Object.keys(d).length === 0) return false;
    return ['tracking_error', 'volatility', 'return_risk_ratio', 'max_drawdown']
      .some((k) => d[k] != null && Number(d[k]) !== 0);
  }), [filteredCodes, riskMetricsData]);

  // ─── Holdings: group by asset class ────────────────────────────
  const holdingsGroups = useMemo(() => {
    if (!holdingsData?.holdings) return [];
    const groups = {};
    holdingsData.holdings.forEach((h) => {
      const ac = MERGE_ASSET_CLASSES[h.asset_class_group] || h.asset_class_group || 'Other';
      if (!groups[ac]) groups[ac] = [];
      groups[ac].push(h);
    });
    return Object.entries(groups).map(([ac, holdings]) => ({
      assetClass: ac,
      holdings,
      totals: filteredCodes.reduce((acc, code) => {
        acc[code] = holdings.reduce((sum, h) => sum + (h.weights?.[code] || 0), 0);
        return acc;
      }, {}),
    }));
  }, [holdingsData, filteredCodes]);

  // ─── Panel 4: derived SAA data ──────────────────────────────────
  // Asset classes to show as rows: those with a non-zero SAA weight in at least one
  // selected portfolio. An asset class that's 0% (or absent) across every selected
  // portfolio is dropped so the table doesn't carry all-zero rows.
  const saaAssetClasses = useMemo(() => {
    const totals = {};
    filteredCodes.forEach((code) => {
      (allocationData[code] || []).forEach((row) => {
        const mapped = MERGE_ASSET_CLASSES[row.asset_class_group] || row.asset_class_group;
        totals[mapped] = (totals[mapped] || 0) + (row.target_percent || 0);
      });
    });
    return Object.keys(totals).filter((ac) => Math.abs(totals[ac]) > 1e-9);
  }, [allocationData, filteredCodes]);

  // Merged allocation data for rendering (EM Equities → International Equities)
  const mergedAllocationData = useMemo(() => {
    const result = {};
    filteredCodes.forEach((code) => {
      const raw = allocationData[code] || [];
      const map = {};
      raw.forEach((r) => {
        const key = MERGE_ASSET_CLASSES[r.asset_class_group] || r.asset_class_group;
        if (!map[key]) map[key] = { ...r, asset_class_group: key, target_percent: 0 };
        map[key].target_percent += (r.target_percent || 0);
      });
      result[code] = Object.values(map);
    });
    return result;
  }, [allocationData, filteredCodes]);

  // Portfolios without SAA data in factsheet — use override ranges
  const NO_SAA_OVERRIDES = {
    DDP: { timeHorizon: '5+', growthRange: '10-80', defenseRange: '10-80' },
  };

  const saaSummary = useMemo(() => {
    const summary = {};
    filteredCodes.forEach((code) => {
      // Merge allocation rows (e.g. EM Equities → International Equities)
      const rawAlloc = allocationData[code] || [];
      const mergedMap = {};
      rawAlloc.forEach((r) => {
        const key = MERGE_ASSET_CLASSES[r.asset_class_group] || r.asset_class_group;
        if (!mergedMap[key]) mergedMap[key] = { ...r, asset_class_group: key, target_percent: 0 };
        mergedMap[key].target_percent += (r.target_percent || 0);
      });
      const alloc = Object.values(mergedMap);
      const hasAlloc = alloc.length > 0;
      const overrides = NO_SAA_OVERRIDES[code];

      const hasSplitGA = alloc.some((r) => r.asset_class_group === 'Global Alpha - G');
      const growthTotal = alloc
        .filter((r) => GROWTH_ASSET_CLASSES.includes(r.asset_class_group)
          && !(hasSplitGA && r.asset_class_group === 'Global Alpha'))
        .reduce((s, r) => s + (r.target_percent || 0), 0);
      const totalAlloc = alloc.reduce((s, r) => s + (r.target_percent || 0), 0);
      const defenseTotal = totalAlloc - growthTotal;

      // Neutral growth from registry, or calculate from allocation
      const ge = codeToGrowthExposure[code];
      const neutralPct = ge != null ? Math.round(ge * 100) : (hasAlloc ? Math.round(growthTotal) : null);

      let growthRange, defenseRange, timeHorizon;

      if (overrides) {
        growthRange = overrides.growthRange;
        defenseRange = overrides.defenseRange;
        timeHorizon = overrides.timeHorizon;
      } else if (neutralPct != null) {
        const growthLo = Math.max(0, neutralPct - 10);
        const growthHi = Math.min(100, neutralPct + 10);
        const defenseLo = Math.max(0, (100 - neutralPct) - 10);
        const defenseHi = Math.min(100, (100 - neutralPct) + 10);
        growthRange = `${growthLo}-${growthHi}`;
        defenseRange = `${defenseLo}-${defenseHi}`;

        timeHorizon = '3+';
        if (neutralPct > 70) timeHorizon = '9+';
        else if (neutralPct > 50) timeHorizon = '7+';
        else if (neutralPct > 30) timeHorizon = '5+';
      } else {
        growthRange = '—';
        defenseRange = '—';
        timeHorizon = '—';
      }

      // The registry's published growth band, when set, is the source of truth
      // for the ranges. The defensive band is its complement (100 − hi .. 100 − lo).
      const registryBand = parseGrowthBand(codeToGrowthRange[code]);
      if (registryBand) {
        growthRange = `${registryBand.lo}-${registryBand.hi}`;
        defenseRange = `${100 - registryBand.hi}-${100 - registryBand.lo}`;
      }

      summary[code] = {
        growthTotal: hasAlloc ? Math.round(growthTotal) : null,
        defenseTotal: hasAlloc ? Math.round(defenseTotal) : null,
        growthRange,
        defenseRange,
        neutralGrowth: neutralPct != null ? neutralPct : '—',
        timeHorizon,
      };
    });
    return summary;
  }, [allocationData, filteredCodes, codeToGrowthExposure, codeToGrowthRange]);

  // ─── Tilts: compute signal rows ────────────────────────────────
  // Merge tilts data (e.g. EM Equities → International Equities)
  const mergedTiltsData = useMemo(() => {
    if (!tiltsData?.latest || !tiltsData?.series) return tiltsData;
    const mergedLatest = {};
    const mergedSeries = {};
    const mergedLatestTarget = {};
    Object.entries(tiltsData.latest).forEach(([ac, tilt]) => {
      const key = MERGE_ASSET_CLASSES[ac] || ac;
      mergedLatest[key] = (mergedLatest[key] || 0) + tilt;
    });
    Object.entries(tiltsData.latest_target || {}).forEach(([ac, target]) => {
      const key = MERGE_ASSET_CLASSES[ac] || ac;
      mergedLatestTarget[key] = (mergedLatestTarget[key] || 0) + target;
    });
    Object.entries(tiltsData.series).forEach(([ac, arr]) => {
      const key = MERGE_ASSET_CLASSES[ac] || ac;
      if (!mergedSeries[key]) {
        mergedSeries[key] = [...arr];
      } else {
        mergedSeries[key] = mergedSeries[key].map((v, i) => v + (arr[i] ?? 0));
      }
    });
    return { ...tiltsData, latest: mergedLatest, series: mergedSeries, latest_target: mergedLatestTarget };
  }, [tiltsData]);

  const signalRows = useMemo(() => {
    if (!mergedTiltsData?.latest || !mergedTiltsData?.series) return [];
    return Object.entries(mergedTiltsData.latest).map(([ac, tilt]) => {
      const exposure = mergedTiltsData.latest_target?.[ac] ?? 0;   // actual target weight %
      return {
        assetClass: ac,
        tilt,
        exposure,
        neutral: exposure - tilt,                                  // SAA weight = exposure − tilt
        position: getPosition(tilt, ac, exposure),
      };
    });
  }, [mergedTiltsData]);

  // Rows: every broad asset class (from investment config, via the tilts
  // endpoint) that carries either an SAA (neutral) weight or actual exposure —
  // empty buckets are hidden. Ordered by the config broad-class display order.
  const filteredSignalRows = useMemo(() => {
    const EPS = 0.05;   // treat sub-0.05% as zero
    const order = {};
    (registryData?.broad_asset_classes || []).forEach((b, i) => { order[b.name] = i; });
    return signalRows
      .filter((r) => Math.abs(r.exposure) > EPS || Math.abs(r.neutral) > EPS)
      .sort((a, b) => (order[a.assetClass] ?? 900) - (order[b.assetClass] ?? 900));
  }, [signalRows, registryData]);

  // Growth vs defensive summary — the backend supplies the growth tilt series
  // weighted by each SAC's config growth_exposure, so defensive alternatives
  // stay defensive even though they share the Global Alpha broad bucket.
  const growthDefensiveSummary = useMemo(() => {
    const g = tiltsData?.growth;
    if (!g?.series?.length) return null;
    const growthTilt = g.series[g.series.length - 1] ?? 0;
    return {
      assetClass: 'Total Growth vs Defensive',
      tilt: growthTilt,
      position: getPosition(growthTilt, 'Total Growth vs Defensive'),
      isSummary: true,
    };
  }, [tiltsData]);

  // ─── Handlers ──────────────────────────────────────────────────
  const toggleAssetClass = useCallback((ac) => {
    setExpandedAssetClasses((prev) => ({ ...prev, [ac]: !prev[ac] }));
  }, []);

  const handleFundHover = useCallback((e, trCode) => {
    if (!trCode || !fundPerfData[trCode]) {
      setHoveredFund(null);
      return;
    }
    setHoveredFund(trCode);
    setTooltipPos({ x: e.clientX, y: e.clientY });
  }, [fundPerfData]);

  const handleFundLeave = useCallback(() => {
    setHoveredFund(null);
  }, []);

  // ─── Loading / error states ────────────────────────────────────
  if (initialLoading) {
    return (
      <div className="pdash-container">
        <div className="pdash-loading">
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
          <span>Loading portfolios...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdash-container">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (allCodes.length === 0) {
    return (
      <div className="pdash-container">
        <div className="pdash-header">
          <h2>Portfolio Dashboard</h2>
        </div>
        <div className="pdash-empty">No portfolios available for your domain.</div>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="pdash-container">
      <div className="pdash-header">
        <h2>Portfolio Dashboard</h2>
        <p>Overview of your model portfolio suite</p>
        <p className="pdash-subnote" style={{ fontSize: '0.8rem', color: 'var(--gray-color, #6c757d)', fontStyle: 'italic', marginTop: '0.25rem', maxWidth: 720 }}>
          Please note: figures reflect the most recent platform shadow available and may lag your live holdings. Timing can vary with recent trading activity and how frequently each platform refreshes its shadow reporting.
        </p>
      </div>

      {availableTabs.length > 1 && (
        <div className="pdash-suite-tabs">
          {availableTabs.map((tab) => (
            <button
              key={tab.label}
              className={`pdash-suite-tab${selectedSuite === tab.label ? ' active' : ''}`}
              onClick={() => setSelectedSuite(tab.label)}
              title={tab.clients?.length ? tab.clients.join(', ') : undefined}
            >
              {tab.label}
              {isDrummondUser && tab.tailored && tab.clients?.length > 0 && (
                <span className="pdash-suite-tab-client">{tab.clients.join(', ')}</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="pdash-grid">
        {/* ─── Panel 1: Holdings ─────────────────────────────── */}
        <div className="pdash-panel" style={{ height: '700px' }}>
          <div className="pdash-panel-header">
            <h3>Target Weights</h3>
          </div>
          <div className="pdash-panel-body">
            {loadingHoldings ? (
              <div className="pdash-spinner">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span>Loading holdings...</span>
              </div>
            ) : holdingsGroups.length > 0 ? (
              <div className="pdash-holdings-scroll">
                <table className="pdash-holdings-table">
                  <thead>
                    <tr>
                      <th className="pdash-sticky-col">Fund Name</th>
                      {filteredCodes.map((code) => (
                        <th key={code}>{codeToName[code] || code}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {holdingsGroups.map((group) => {
                      const isExpanded = !!expandedAssetClasses[group.assetClass];
                      return (
                        <React.Fragment key={group.assetClass}>
                          {/* Asset class row (collapsed = shows totals) */}
                          <tr
                            className="pdash-ac-row"
                            onClick={() => toggleAssetClass(group.assetClass)}
                          >
                            <td className="pdash-sticky-col pdash-ac-name">
                              <span
                                className="pdash-ac-arrow"
                                style={{ transform: isExpanded ? 'rotate(90deg)' : 'none' }}
                              >
                                ▶
                              </span>
                              {group.assetClass}
                            </td>
                            {filteredCodes.map((code) => (
                              <td key={code} className="pdash-ac-weight">
                                {fmtPct1(group.totals[code])}
                              </td>
                            ))}
                          </tr>
                          {/* Expanded: individual fund rows */}
                          {isExpanded && group.holdings.map((h) => (
                            <tr
                              key={`${h.asset_name}-${h.tr_code}`}
                              className="pdash-fund-row"
                              onMouseEnter={(e) => handleFundHover(e, h.tr_code)}
                              onMouseMove={(e) => {
                                if (hoveredFund === h.tr_code) {
                                  setTooltipPos({ x: e.clientX, y: e.clientY });
                                }
                              }}
                              onMouseLeave={handleFundLeave}
                            >
                              <td className="pdash-sticky-col pdash-fund-name">
                                {h.asset_name}
                              </td>
                              {filteredCodes.map((code) => (
                                <td key={code}>
                                  {h.weights?.[code] != null ? fmtPct(h.weights[code]) : '—'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="pdash-no-data">No holdings data available.</div>
            )}
          </div>
        </div>

        {/* ─── Panel 2: Performance ──────────────────────────── */}
        <div className="pdash-panel">
          <div className="pdash-panel-header pdash-panel-header-with-toggle">
            <h3>Performance</h3>
            {performanceData?.as_at_date && (
              <span className="pdash-as-at">As at {performanceData.as_at_date}</span>
            )}
            <div className="pdash-perf-toggle">
              <button
                className={`pdash-toggle-btn${perfView === 'periods' ? ' active' : ''}`}
                onClick={() => setPerfView('periods')}
              >
                Periods
              </button>
              <button
                className={`pdash-toggle-btn${perfView === 'fy' ? ' active' : ''}`}
                onClick={() => setPerfView('fy')}
              >
                Financial Year
              </button>
              <button
                className={`pdash-toggle-btn${perfView === 'risk' ? ' active' : ''}`}
                onClick={() => setPerfView('risk')}
              >
                Risk
              </button>
            </div>
          </div>
          <div className="pdash-panel-body">
            {/* ── Periods view ── */}
            {perfView === 'periods' && (
              loadingPerf ? (
                <div className="pdash-spinner">
                  <div className="spinner-border spinner-border-sm text-primary" role="status" />
                  <span>Loading performance...</span>
                </div>
              ) : (() => {
                // Omit portfolios with no usable performance: either every period is
                // null (e.g. those flagged show_performance=False, which return
                // performance: {}), or every period is exactly 0.0% — an all-zero row
                // means no real return data, so hide the whole block rather than
                // rendering a wall of 0.0%.
                const visiblePortfolios = (performanceData?.portfolios || [])
                  .filter((pf) => filteredCodes.includes(pf.portfolio_code))
                  .filter((pf) => PERF_TABLE_PERIODS.some((p) => {
                    const v = (pf.performance || {})[p.key]?.portfolio;
                    return v != null && Number(v) !== 0;
                  }));
                return visiblePortfolios.length > 0 ? (
                <div className="pdash-perf-scroll">
                  <table className="pdash-perf-table">
                    <thead>
                      <tr>
                        <th className="pdash-perf-name-col">Performance</th>
                        {PERF_TABLE_PERIODS.map((p) => (
                          <th key={p.key}>{p.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let rowNum = 0;
                        return visiblePortfolios.map((pf, idx) => {
                          const perf = pf.performance || {};
                          if (idx > 0) rowNum++;
                          const r1 = rowNum++;
                          const r2 = rowNum++;
                          const r3 = rowNum++;
                          return (
                            <React.Fragment key={pf.portfolio_code}>
                              <tr className={`pdash-perf-portfolio-row${r1 % 2 === 1 ? ' pdash-perf-shaded' : ''}`}>
                                <td className="pdash-perf-label pdash-perf-portfolio-name">
                                  {codeToName[pf.portfolio_code] || pf.portfolio_name || pf.portfolio_code}
                                </td>
                                {PERF_TABLE_PERIODS.map((p) => (
                                  <td key={p.key} className="pdash-perf-val">
                                    {perf[p.key]?.portfolio != null ? fmtPct1(perf[p.key].portfolio) : '—'}
                                  </td>
                                ))}
                              </tr>
                              <tr className={`pdash-perf-ami-row${r2 % 2 === 1 ? ' pdash-perf-shaded' : ''}`}>
                                <td className="pdash-perf-label pdash-perf-ami-name">
                                  {pf.ami_benchmark || 'AMI Benchmark'}
                                </td>
                                {PERF_TABLE_PERIODS.map((p) => (
                                  <td key={p.key} className="pdash-perf-val">
                                    {perf[p.key]?.ami != null ? fmtPct1(perf[p.key].ami) : '—'}
                                  </td>
                                ))}
                              </tr>
                              <tr className={`pdash-perf-diff-row${r3 % 2 === 1 ? ' pdash-perf-shaded' : ''}`}>
                                <td className="pdash-perf-label pdash-perf-diff-name">Difference</td>
                                {PERF_TABLE_PERIODS.map((p) => {
                                  const diff = perf[p.key]?.difference;
                                  return (
                                    <td
                                      key={p.key}
                                      className={`pdash-perf-diff-val ${diff > 0 ? 'pos' : diff < 0 ? 'neg' : ''}`}
                                    >
                                      {diff != null ? fmtPct1(diff) : '—'}
                                    </td>
                                  );
                                })}
                              </tr>
                              {idx < visiblePortfolios.length - 1 && (
                                <tr className="pdash-perf-spacer"><td colSpan={PERF_TABLE_PERIODS.length + 1} /></tr>
                              )}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
                ) : (
                  <div className="pdash-no-data">No performance data available.</div>
                );
              })()
            )}

            {/* ── Financial Year view ── */}
            {perfView === 'fy' && (
              loadingFY ? (
                <div className="pdash-spinner">
                  <div className="spinner-border spinner-border-sm text-primary" role="status" />
                  <span>Loading financial year returns...</span>
                </div>
              ) : fyColumns.length > 0 && fyVisibleCodes.length > 0 ? (
                <div className="pdash-perf-scroll">
                  <table className="pdash-perf-table">
                    <thead>
                      <tr>
                        <th className="pdash-perf-name-col">Financial Year</th>
                        {fyColumns.map((col) => (
                          <th key={col.key}>{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let rowNum = 0;
                        return fyVisibleCodes.map((code, idx) => {
                          const fyData = fyReturnsData[code];
                          if (!fyData?.columns) return null;
                          const colMap = {};
                          fyData.columns.forEach((c) => { colMap[c.key] = c; });
                          if (idx > 0) rowNum++;
                          const r1 = rowNum++;
                          const r2 = rowNum++;
                          const r3 = rowNum++;
                          return (
                            <React.Fragment key={code}>
                              <tr className={`pdash-perf-portfolio-row${r1 % 2 === 1 ? ' pdash-perf-shaded' : ''}`}>
                                <td className="pdash-perf-label pdash-perf-portfolio-name">
                                  {codeToName[code] || code}
                                </td>
                                {fyColumns.map((col) => {
                                  const cell = colMap[col.key];
                                  const isPartial = cell?.partial;
                                  return (
                                    <td key={col.key} className="pdash-perf-val">
                                      {cell?.portfolio != null
                                        ? `${fmtPct1(cell.portfolio)}${isPartial ? '*' : ''}`
                                        : '—'}
                                    </td>
                                  );
                                })}
                              </tr>
                              <tr className={`pdash-perf-ami-row${r2 % 2 === 1 ? ' pdash-perf-shaded' : ''}`}>
                                <td className="pdash-perf-label pdash-perf-ami-name">{codeToBenchmark[code] || 'Peer Benchmark'}</td>
                                {fyColumns.map((col) => {
                                  const cell = colMap[col.key];
                                  const isPartial = cell?.partial;
                                  return (
                                    <td key={col.key} className="pdash-perf-val">
                                      {cell?.peer != null
                                        ? `${fmtPct1(cell.peer)}${isPartial ? '*' : ''}`
                                        : '—'}
                                    </td>
                                  );
                                })}
                              </tr>
                              <tr className={`pdash-perf-diff-row${r3 % 2 === 1 ? ' pdash-perf-shaded' : ''}`}>
                                <td className="pdash-perf-label pdash-perf-diff-name">Difference</td>
                                {fyColumns.map((col) => {
                                  const cell = colMap[col.key];
                                  const pVal = cell?.portfolio;
                                  const bVal = cell?.peer;
                                  const diff = pVal != null && bVal != null ? pVal - bVal : null;
                                  return (
                                    <td
                                      key={col.key}
                                      className={`pdash-perf-diff-val ${diff > 0 ? 'pos' : diff < 0 ? 'neg' : ''}`}
                                    >
                                      {diff != null ? fmtPct1(diff) : '—'}
                                    </td>
                                  );
                                })}
                              </tr>
                              {idx < fyVisibleCodes.length - 1 && (
                                <tr className="pdash-perf-spacer"><td colSpan={fyColumns.length + 1} /></tr>
                              )}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                  {/* Per-portfolio footnotes */}
                  {(() => {
                    // Determine the current FY key (last column, highest year number)
                    const lastFyKey = fyColumns[fyColumns.length - 1]?.key;
                    const footnotes = [];

                    // Format a YYYY-MM-DD as-at date as "31 May 2026" (avoid TZ day-shift)
                    const fmtAsAt = (iso) => {
                      if (!iso) return null;
                      const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
                      return isNaN(d) ? null : d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
                    };

                    // Check each portfolio for partial years
                    fyVisibleCodes.forEach((code) => {
                      const cols = fyReturnsData[code]?.columns;
                      if (!cols) return;
                      const partialCols = cols.filter((c) => c.partial && c.portfolio != null);
                      if (partialCols.length === 0) return;

                      const name = codeToName[code] || code;
                      const partialKeys = partialCols.map((c) => c.key);
                      const hasCurrentFY = partialKeys.includes(lastFyKey);
                      const inceptionPartials = partialKeys.filter((k) => k !== lastFyKey);
                      // Name the period covered by the current-FY YTD partial, when known
                      const asAt = fmtAsAt(partialCols.find((c) => c.key === lastFyKey)?.as_at);
                      const ytdText = asAt ? `${lastFyKey} year-to-date to ${asAt}` : `${lastFyKey} year-to-date`;

                      if (inceptionPartials.length > 0 && hasCurrentFY) {
                        footnotes.push(`${name}: ${inceptionPartials.join(', ')} partial (inception mid-year), ${ytdText}`);
                      } else if (inceptionPartials.length > 0) {
                        footnotes.push(`${name}: ${inceptionPartials.join(', ')} partial (inception mid-year)`);
                      } else if (hasCurrentFY) {
                        footnotes.push(`${name}: ${ytdText}`);
                      }
                    });

                    // If every portfolio only has the current FY as partial, simplify
                    const allOnlyCurrentFY = fyVisibleCodes.every((code) => {
                      const cols = fyReturnsData[code]?.columns;
                      if (!cols) return true;
                      const partials = cols.filter((c) => c.partial && c.portfolio != null);
                      return partials.length <= 1 && partials.every((c) => c.key === lastFyKey);
                    });

                    if (footnotes.length === 0) return null;

                    if (allOnlyCurrentFY) {
                      // Show the as-at date only if every portfolio shares the same one
                      const asAtSet = new Set(
                        fyVisibleCodes
                          .map((code) => (fyReturnsData[code]?.columns || [])
                            .find((c) => c.key === lastFyKey && c.partial)?.as_at)
                          .filter(Boolean)
                      );
                      const commonAsAt = asAtSet.size === 1 ? fmtAsAt([...asAtSet][0]) : null;
                      return (
                        <div className="pdash-perf-footnote">
                          *{lastFyKey} returns are year-to-date{commonAsAt ? ` to ${commonAsAt}` : ''}
                        </div>
                      );
                    }

                    return (
                      <div className="pdash-perf-footnotes">
                        {footnotes.map((note, i) => (
                          <div key={i} className="pdash-perf-footnote">*{note}</div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="pdash-no-data">No financial year data available.</div>
              )
            )}

            {/* ── Risk Metrics view ── */}
            {perfView === 'risk' && (
              loadingRisk ? (
                <div className="pdash-spinner">
                  <div className="spinner-border spinner-border-sm text-primary" role="status" />
                  <span>Loading risk metrics...</span>
                </div>
              ) : riskVisibleCodes.length > 0 ? (
                <div className="pdash-perf-scroll">
                  <table className="pdash-perf-table">
                    <thead>
                      <tr>
                        <th className="pdash-perf-name-col">Risk Metrics</th>
                        <th>Tracking Error</th>
                        <th>Volatility</th>
                        <th>Return / Risk</th>
                        <th>Max Drawdown</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let rowNum = 0;
                        const rows = [];
                        const codesWithData = riskVisibleCodes;
                        codesWithData.forEach((code, idx) => {
                          const d = riskMetricsData[code];
                          if (idx > 0) rowNum++;
                          const r1 = rowNum++;
                          const r2 = rowNum++;
                          const r3 = rowNum++;
                          rows.push(
                            <React.Fragment key={code}>
                              <tr className={`pdash-perf-portfolio-row${r1 % 2 === 1 ? ' pdash-perf-shaded' : ''}`}>
                                <td className="pdash-perf-label pdash-perf-portfolio-name">
                                  {codeToName[code] || code}
                                </td>
                                <td className="pdash-perf-val">{d.tracking_error != null ? `${d.tracking_error.toFixed(1)}%` : '—'}</td>
                                <td className="pdash-perf-val">{d.volatility != null ? `${d.volatility.toFixed(1)}%` : '—'}</td>
                                <td className="pdash-perf-val">{d.return_risk_ratio != null ? d.return_risk_ratio.toFixed(2) : '—'}</td>
                                <td className="pdash-perf-val">{d.max_drawdown != null ? `${d.max_drawdown.toFixed(1)}%` : '—'}</td>
                              </tr>
                              <tr className={`pdash-perf-ami-row${r2 % 2 === 1 ? ' pdash-perf-shaded' : ''}`}>
                                <td className="pdash-perf-label pdash-perf-ami-name">{codeToBenchmark[code] || 'Benchmark'}</td>
                                <td className="pdash-perf-val"></td>
                                <td className="pdash-perf-val">{d.benchmark_volatility != null ? `${d.benchmark_volatility.toFixed(1)}%` : '—'}</td>
                                <td className="pdash-perf-val">{d.benchmark_return_risk_ratio != null ? d.benchmark_return_risk_ratio.toFixed(2) : '—'}</td>
                                <td className="pdash-perf-val">{d.benchmark_max_drawdown != null ? `${d.benchmark_max_drawdown.toFixed(1)}%` : '—'}</td>
                              </tr>
                              <tr className={`pdash-perf-diff-row${r3 % 2 === 1 ? ' pdash-perf-shaded' : ''}`}>
                                <td className="pdash-perf-label pdash-perf-diff-name">Difference</td>
                                {(() => {
                                  const volDiff = (d.volatility != null && d.benchmark_volatility != null)
                                    ? d.volatility - d.benchmark_volatility : null;
                                  const rrDiff = (d.return_risk_ratio != null && d.benchmark_return_risk_ratio != null)
                                    ? d.return_risk_ratio - d.benchmark_return_risk_ratio : null;
                                  const ddDiff = (d.max_drawdown != null && d.benchmark_max_drawdown != null)
                                    ? d.max_drawdown - d.benchmark_max_drawdown : null;
                                  return (
                                    <>
                                      <td className="pdash-perf-diff-val"></td>
                                      <td className={`pdash-perf-diff-val ${volDiff > 0 ? 'neg' : volDiff < 0 ? 'pos' : ''}`}>
                                        {volDiff != null ? `${volDiff.toFixed(1)}%` : '—'}
                                      </td>
                                      <td className={`pdash-perf-diff-val ${rrDiff > 0 ? 'pos' : rrDiff < 0 ? 'neg' : ''}`}>
                                        {rrDiff != null ? rrDiff.toFixed(2) : '—'}
                                      </td>
                                      <td className={`pdash-perf-diff-val ${ddDiff > 0 ? 'pos' : ddDiff < 0 ? 'neg' : ''}`}>
                                        {ddDiff != null ? `${ddDiff.toFixed(1)}%` : '—'}
                                      </td>
                                    </>
                                  );
                                })()}
                              </tr>
                              {idx < codesWithData.length - 1 && (
                                <tr className="pdash-perf-spacer"><td colSpan={5} /></tr>
                              )}
                            </React.Fragment>
                          );
                        });
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="pdash-no-data">No risk metrics available.</div>
              )
            )}
            {perfView === 'fy' && fyGapNotes(fyVisibleCodes, fyReturnsData, (c) => codeToName[c] || c).map((n, i) => (
              <p key={`fygap${i}`} className="pdash-perf-disclaimer">{n}</p>
            ))}
            {perfView === 'risk' && riskVisibleCodes.length > 0 && (
              <>
                <p className="pdash-perf-disclaimer">{RISK_METHOD_NOTE}</p>
                {riskGapNotes(riskVisibleCodes, riskMetricsData, (c) => codeToName[c] || c).map((n, i) => (
                  <p key={`riskgap${i}`} className="pdash-perf-disclaimer">{n}</p>
                ))}
              </>
            )}
            <p className="pdash-perf-disclaimer">
              {perfNote.loading ? '' : disclaimerStateText(perfNote, 'perf_general_note')}{inceptionDisclaimer ? ` ${inceptionDisclaimer}` : ''}{performanceSourceLines.length ? ` ${performanceSourceLines.join(' ')}` : ''}
            </p>
          </div>
        </div>

        {/* ─── Panel 3: SAA Positioning ──────────────────────────
            Strategic Series: house average tilts (DS portfolios vs Drummond SAA).
            Tailored suites: suite-average tilts, each portfolio vs its own SAA.
            All other suites: panel hidden. */}
        {showPositioning && (
        <div className="pdash-panel">
          <div className="pdash-panel-header">
            <h3>SAA Positioning</h3>
            {holdingsData?.as_at_date && (
              <span className="pdash-as-at">As at {holdingsData.as_at_date}</span>
            )}
          </div>
          <div className="pdash-panel-body">
            {loadingTilts ? (
              <div className="pdash-spinner">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span>Loading tilts...</span>
              </div>
            ) : filteredSignalRows.length > 0 ? (
              <div className="pdash-signal-scroll">
                <table className="pdash-signal-table">
                  <thead>
                    <tr>
                      <th>Asset Class</th>
                      {POSITION_COLUMNS.map((col) => (
                        <th key={col} className="pdash-signal-dot-col">
                          {POSITION_LABELS[col]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSignalRows.map((row) => (
                      <tr key={row.assetClass}>
                        <td className="pdash-signal-ac">{row.assetClass}</td>
                        {POSITION_COLUMNS.map((col) => (
                          <td key={col} className="pdash-signal-dot-cell">
                            {row.position === col ? (
                              <span
                                className="pdash-dot-filled"
                                style={{ backgroundColor: POSITION_DOT_COLORS[col] }}
                              />
                            ) : (
                              <span className="pdash-dot-hollow">○</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {/* Summary row */}
                    {growthDefensiveSummary && (
                      <tr className="pdash-signal-summary-row">
                        <td className="pdash-signal-ac pdash-signal-summary-label">
                          {growthDefensiveSummary.assetClass}
                        </td>
                        {POSITION_COLUMNS.map((col) => (
                          <td key={col} className="pdash-signal-dot-cell">
                            {growthDefensiveSummary.position === col ? (
                              <span
                                className="pdash-dot-filled"
                                style={{ backgroundColor: POSITION_DOT_COLORS[col] }}
                              />
                            ) : (
                              <span className="pdash-dot-hollow">○</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="pdash-no-data">No tilt data available.</div>
            )}
            {filteredSignalRows.length > 0 && (
              <p className="pdash-signal-note">
                Tilt = current target weight less the strategic (SAA) weight for the live financial
                year, calculated per sub-asset class and summed into broad asset classes, then
                averaged across {activeTab?.tailored
                  ? 'every portfolio in this suite (each against its own SAA)'
                  : 'the Drummond Strategic portfolios'}.
                Neutral is within ±0.5%; over/underweight from 0.5%; strong from 5% for the major
                asset classes (3% otherwise). An asset class with an SAA weight but no holdings
                shows as strong underweight, and classes with neither exposure nor SAA weight are
                hidden. Total Growth vs Defensive weights each sub-asset class tilt by its growth
                exposure.
              </p>
            )}
          </div>
        </div>
        )}

        {/* ─── Panel 4: SAA Information ─────────────────────── */}
        <div className="pdash-panel">
          <div className="pdash-panel-header">
            <h3>SAA Information</h3>
          </div>
          <div className="pdash-panel-body">
            {loadingSAA ? (
              <div className="pdash-spinner">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <span>Loading SAA data...</span>
              </div>
            ) : (saaAssetClasses.length > 0 || Object.keys(blenderData).length > 0) ? (
              <div className="pdash-saa-scroll">
                <table className="pdash-saa-table">
                  <thead>
                    <tr>
                      <th className="pdash-saa-name-col"></th>
                      {filteredCodes.map((code) => (
                        <th key={code}>{codeToName[code] || code}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* ── Section: Asset Allocation ── */}
                    <tr className="pdash-saa-section-row">
                      <td colSpan={filteredCodes.length + 1} className="pdash-saa-section-td">Asset Allocation</td>
                    </tr>
                    {saaAssetClasses.map((ac, i) => (
                      <tr key={ac} className={i % 2 === 1 ? 'pdash-saa-shaded' : ''}>
                        <td className="pdash-saa-ac-name">{ac}</td>
                        {filteredCodes.map((code) => {
                          const alloc = mergedAllocationData[code];
                          if (!alloc) return <td key={code}>—</td>;
                          const row = alloc.find((r) => r.asset_class_group === ac);
                          return (
                            <td key={code}>{row ? `${Math.round(row.target_percent)}%` : '0%'}</td>
                          );
                        })}
                      </tr>
                    ))}
                    {/* Summary rows */}
                    <tr className="pdash-saa-spacer"><td colSpan={filteredCodes.length + 1} /></tr>
                    <tr className="pdash-saa-summary-row">
                      <td className="pdash-saa-ac-name">Growth Total</td>
                      {filteredCodes.map((code) => (
                        <td key={code}>{saaSummary[code]?.growthTotal != null ? `${saaSummary[code].growthTotal}%` : '—'}</td>
                      ))}
                    </tr>
                    <tr className="pdash-saa-summary-row pdash-saa-shaded">
                      <td className="pdash-saa-ac-name">Defence Total</td>
                      {filteredCodes.map((code) => (
                        <td key={code}>{saaSummary[code]?.defenseTotal != null ? `${saaSummary[code].defenseTotal}%` : '—'}</td>
                      ))}
                    </tr>
                    <tr className="pdash-saa-summary-row">
                      <td className="pdash-saa-ac-name">Growth Range</td>
                      {filteredCodes.map((code) => (
                        <td key={code}>{formatBandPct(saaSummary[code]?.growthRange)}</td>
                      ))}
                    </tr>
                    <tr className="pdash-saa-summary-row pdash-saa-shaded">
                      <td className="pdash-saa-ac-name">Defence Range</td>
                      {filteredCodes.map((code) => (
                        <td key={code}>{formatBandPct(saaSummary[code]?.defenseRange)}</td>
                      ))}
                    </tr>
                    <tr className="pdash-saa-summary-row">
                      <td className="pdash-saa-ac-name">Neutral Growth Exposure</td>
                      {filteredCodes.map((code) => {
                        const v = saaSummary[code]?.neutralGrowth;
                        return <td key={code}>{v != null && v !== '—' ? `${v}%` : '—'}</td>;
                      })}
                    </tr>

                    {/* ── Section: Risk Metrics ── */}
                    <tr className="pdash-saa-section-row">
                      <td colSpan={filteredCodes.length + 1} className="pdash-saa-section-td">Risk Metrics</td>
                    </tr>
                    <tr>
                      <td className="pdash-saa-ac-name">Time Horizon</td>
                      {filteredCodes.map((code) => (
                        <td key={code}>{saaSummary[code]?.timeHorizon ?? '—'}</td>
                      ))}
                    </tr>
                    <tr className="pdash-saa-shaded">
                      <td className="pdash-saa-ac-name">Expected Return</td>
                      {filteredCodes.map((code) => {
                        const val = blenderData[code]?.return;
                        return <td key={code}>{val != null ? `${(val * 100).toFixed(1)}%` : '—'}</td>;
                      })}
                    </tr>
                    <tr>
                      <td className="pdash-saa-ac-name">Expected Volatility</td>
                      {filteredCodes.map((code) => {
                        const val = blenderData[code]?.volatility;
                        return <td key={code}>{val != null ? `${(val * 100).toFixed(1)}%` : '—'}</td>;
                      })}
                    </tr>
                    <tr className="pdash-saa-shaded">
                      <td className="pdash-saa-ac-name" title="RG 97 management fees and costs — from PDS fee data (mgmt + indirect − rebates)">Mgmt Fees &amp; Costs</td>
                      {filteredCodes.map((code) => {
                        const val = blenderData[code]?.icr ?? blenderData[code]?.income;
                        return <td key={code}>{val != null ? `${val.toFixed(2)}%` : '—'}</td>;
                      })}
                    </tr>
                    <tr>
                      <td className="pdash-saa-ac-name">Est. Negative Years (20yr)</td>
                      {filteredCodes.map((code) => {
                        const val = blenderData[code]?.stochastics?.metrics?.expected_negative_years;
                        return <td key={code}>{val != null ? val.toFixed(1) : '—'}</td>;
                      })}
                    </tr>
                    <tr className="pdash-saa-shaded">
                      <td className="pdash-saa-ac-name">SRM Risk Band</td>
                      {filteredCodes.map((code) => {
                        const band = blenderData[code]?.srm_risk_band;
                        const label = blenderData[code]?.srm_risk_label;
                        return <td key={code}>{band != null ? `${band} — ${label}` : '—'}</td>;
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="pdash-no-data">No SAA data available.</div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Hover Tooltip ──────────────────────────────────── */}
      {hoveredFund && fundPerfData[hoveredFund] && (
        <div
          className="pdash-tooltip"
          style={{
            left: tooltipPos.x + 12,
            top: tooltipPos.y - 10,
          }}
        >
          <div className="pdash-tooltip-title">
            {fundPerfData[hoveredFund].fund_name || hoveredFund}
          </div>
          <table className="pdash-tooltip-table">
            <tbody>
              {PERF_COLUMNS.filter((col) => col.key !== 'mtd').map((col) => {
                const val = fundPerfData[hoveredFund][col.key];
                return (
                  <tr key={col.key}>
                    <td className="pdash-tooltip-label">{col.label}</td>
                    <td className={`pdash-tooltip-val ${val > 0 ? 'pos' : val < 0 ? 'neg' : ''}`}>
                      {val != null ? `${val > 0 ? '+' : ''}${Number(val).toFixed(2)}%` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PortfolioDashboardApp;
